import { getMaybeOpenPatientPortrait, type PatientPortraitRouteMatch } from './patientPortraitRouteMatch.utils';

import type { IPatientPortraitLifecycleModel } from './PatientPortraitLifecycleModel';
import type { DataRouteMatch, RouterState } from 'react-router';

/**
 * Feeds `PatientPortraitLifecycleModel` straight from the router, so features learn that a patient
 * portrait opened or closed without any layout knowing which features care - and without a component
 * rendering above the products that have nothing to do with portraits.
 *
 * The router only swaps `state.matches` once a navigation commits (a pending navigation updates
 * `state.navigation` alone), so a portrait whose loader redirects or throws is never reported as
 * opened.
 */
export type CommittedRouterState = Pick<RouterState, 'matches' | 'initialized'>;

export type PatientPortraitRouterSubscriber = (state: CommittedRouterState) => void;

/** Only what this infra reads off `createBrowserRouter`'s router - a real router satisfies it. */
export interface PatientPortraitRouter {
	readonly state: CommittedRouterState;
	readonly subscribe: (subscriber: PatientPortraitRouterSubscriber) => () => void;
}

export interface TracePatientPortraitsOnRouterArgs {
	readonly router: PatientPortraitRouter;
	readonly patientPortraitLifecycleModel: IPatientPortraitLifecycleModel;
}

function getPatientPortraitRouteMatch({ params, route }: DataRouteMatch): PatientPortraitRouteMatch {
	return { handle: route.handle, params };
}

export function tracePatientPortraitsOnRouter({
	router,
	patientPortraitLifecycleModel,
}: TracePatientPortraitsOnRouterArgs): () => void {
	function reportOpenPatientPortrait(matches: ReadonlyArray<DataRouteMatch>): void {
		const maybeOpenPatientPortrait = getMaybeOpenPatientPortrait(matches.map(getPatientPortraitRouteMatch));

		if (!maybeOpenPatientPortrait) {
			patientPortraitLifecycleModel.reportNoPatientPortraitOpen();

			return;
		}

		patientPortraitLifecycleModel.reportPatientPortraitOpened(maybeOpenPatientPortrait);
	}

	// `subscribe` never replays the state it was handed, and the very first commit happens before
	// this runs whenever the initial route needs no loader.
	if (router.state.initialized) {
		reportOpenPatientPortrait(router.state.matches);
	}

	return router.subscribe(function reportOpenPatientPortraitOnCommit({ matches, initialized }): void {
		if (!initialized) {
			return;
		}

		reportOpenPatientPortrait(matches);
	});
}
